import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseService } from '../../core';
import { environment } from 'environments/environment';
import { 
    SolicitacaoEnterpriseResponse, 
    AtualizarStatusEnterpriseRequest, 
    AprovarEnterpriseRequest, 
    AprovarEnterpriseResponse,
    StatusSolicitacaoEnterprise,
    LeadEnterpriseResponse,
    StatusLeadEnterprise,
    AtualizarStatusLeadRequest,
    ConverterLeadRequest,
    ConverterLeadResponse,
    EstatisticasLeadEnterprise,
    ListaLeadsResponse
} from 'app/shared/models/ei-models';

@Injectable({
    providedIn: 'root'
})
export class AdminEnterpriseService extends BaseService {
    private controllerEndpoint = environment.urlEdducaImobApi.concat('api/Admin/');

    constructor(private httpClient: HttpClient) {
        super();
    }

    /**
     * Lista todas as solicitações Enterprise com filtros opcionais
     */
    listarSolicitacoes(
        status?: StatusSolicitacaoEnterprise, 
        page: number = 1, 
        pageSize: number = 10,
        loader = true
    ): Observable<{ items: SolicitacaoEnterpriseResponse[], total: number }> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString());
        
        if (status !== undefined && status !== null) {
            params = params.set('status', status.toString());
        }

        return this.httpClient.get<{ items: SolicitacaoEnterpriseResponse[], total: number }>(
            this.controllerEndpoint.concat('SolicitacoesEnterprise'),
            { headers: this.getHeader(loader), params }
        );
    }

    /**
     * Obtém detalhes de uma solicitação específica
     */
    obterSolicitacao(id: number, loader = true): Observable<SolicitacaoEnterpriseResponse> {
        return this.httpClient.get<SolicitacaoEnterpriseResponse>(
            this.controllerEndpoint.concat(`SolicitacoesEnterprise/${id}`),
            { headers: this.getHeader(loader) }
        );
    }

    /**
     * Atualiza o status de uma solicitação
     */
    atualizarStatus(id: number, request: AtualizarStatusEnterpriseRequest, loader = true): Observable<{ message: string }> {
        return this.httpClient.put<{ message: string }>(
            this.controllerEndpoint.concat(`SolicitacoesEnterprise/${id}/status`),
            request,
            { headers: this.getHeader(loader) }
        );
    }

    /**
     * Aprova a solicitação e converte a imobiliária para o plano Enterprise
     * - Atualiza o plano da assinatura para Enterprise (id: 3)
     * - Atualiza a assinatura no Asaas com o novo valor negociado
     * - Marca a solicitação como concluída
     */
    aprovarSolicitacao(id: number, request: AprovarEnterpriseRequest, loader = true): Observable<AprovarEnterpriseResponse> {
        return this.httpClient.post<AprovarEnterpriseResponse>(
            this.controllerEndpoint.concat(`SolicitacoesEnterprise/${id}/aprovar`),
            request,
            { headers: this.getHeader(loader) }
        );
    }

    /**
     * Cancela uma solicitação
     */
    cancelarSolicitacao(id: number, observacoes?: string, loader = true): Observable<{ message: string }> {
        return this.httpClient.post<{ message: string }>(
            this.controllerEndpoint.concat(`SolicitacoesEnterprise/${id}/cancelar`),
            { observacoes },
            { headers: this.getHeader(loader) }
        );
    }

    /**
     * Obtém estatísticas das solicitações
     */
    obterEstatisticas(loader = true): Observable<{
        pendentes: number;
        emAtendimento: number;
        concluidas: number;
        canceladas: number;
        total: number;
    }> {
        return this.httpClient.get<any>(
            this.controllerEndpoint.concat('SolicitacoesEnterprise/estatisticas'),
            { headers: this.getHeader(loader) }
        );
    }

    // ========================================
    // LEADS ENTERPRISE (Landing Page)
    // ========================================

    /**
     * Lista todos os leads Enterprise com filtros opcionais
     */
    listarLeads(
        status?: StatusLeadEnterprise, 
        page: number = 1, 
        pageSize: number = 10,
        loader = true
    ): Observable<ListaLeadsResponse> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString());
        
        if (status !== undefined && status !== null) {
            params = params.set('status', status.toString());
        }

        return this.httpClient.get<ListaLeadsResponse>(
            this.controllerEndpoint.concat('LeadsEnterprise'),
            { headers: this.getHeader(loader), params }
        );
    }

    /**
     * Obtém detalhes de um lead específico
     */
    obterLead(id: number, loader = true): Observable<LeadEnterpriseResponse> {
        return this.httpClient.get<LeadEnterpriseResponse>(
            this.controllerEndpoint.concat(`LeadsEnterprise/${id}`),
            { headers: this.getHeader(loader) }
        );
    }

    /**
     * Atualiza o status de um lead
     */
    atualizarStatusLead(id: number, request: AtualizarStatusLeadRequest, loader = true): Observable<{ message: string }> {
        return this.httpClient.put<{ message: string }>(
            this.controllerEndpoint.concat(`LeadsEnterprise/${id}/status`),
            request,
            { headers: this.getHeader(loader) }
        );
    }

    /**
     * Converte o lead gerando um link de checkout personalizado
     * - Cria um token único para checkout
     * - Define o valor mensal negociado
     * - Marca o lead como em processo de conversão
     */
    converterLead(id: number, request: ConverterLeadRequest, loader = true): Observable<ConverterLeadResponse> {
        return this.httpClient.post<ConverterLeadResponse>(
            this.controllerEndpoint.concat(`LeadsEnterprise/${id}/converter`),
            request,
            { headers: this.getHeader(loader) }
        );
    }

    /**
     * Cancela um lead
     */
    cancelarLead(id: number, observacoes?: string, loader = true): Observable<{ message: string }> {
        return this.httpClient.post<{ message: string }>(
            this.controllerEndpoint.concat(`LeadsEnterprise/${id}/cancelar`),
            { observacoes },
            { headers: this.getHeader(loader) }
        );
    }

    /**
     * Obtém estatísticas dos leads
     */
    obterEstatisticasLeads(loader = true): Observable<EstatisticasLeadEnterprise> {
        return this.httpClient.get<EstatisticasLeadEnterprise>(
            this.controllerEndpoint.concat('LeadsEnterprise/estatisticas'),
            { headers: this.getHeader(loader) }
        );
    }

    /**
     * Reenvia o link de checkout para o lead
     */
    reenviarLinkCheckout(id: number, loader = true): Observable<{ message: string; checkoutUrl: string }> {
        return this.httpClient.post<{ message: string; checkoutUrl: string }>(
            this.controllerEndpoint.concat(`LeadsEnterprise/${id}/reenviar-link`),
            {},
            { headers: this.getHeader(loader) }
        );
    }
}
